import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Card,
  Form,
  notification,
  Typography,
  message,
  Spin,
} from 'antd'
import { LOADING, SUCCEEDED, ERROR } from '@constants/fetchStatuses'
import { updateUser } from '@actions/currentUser'
import {
  selectCurrentUserLicenses,
  selectCurrentUserStatus,
} from '@reducers/currentUser/currentUserSelectors'
import LicenseSetup from '@components/Platform/Shared/Setup/LicenseSetup'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import useSignupPageView from '@components/Signup/SegmentAnalytics/useSignupPageView'
import { USER_INFORMATION_UPDATE } from '@components/Signup/constants'

import './Credentials.scss'

const { Title } = Typography

const Credentials = ({ goToPreviousStep, goToNextStep, stepName, nextStepName }) => {
  useSignupPageView('Account Setup Credentials')
  const track = useSignupAnalyticsTracker()

  const [licenseData, setLicenseData] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const dispatch = useDispatch()
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const licenses = useSelector(selectCurrentUserLicenses)
  const formRef = useRef()

  useEffect(() => {
    if (licenses && licenses.length) {
      setLicenseData(licenses)
    }
  }, [licenses])

  useEffect(() => {
    // key off of status,
    if (currentUserStatus === LOADING) {
      setSubmitting(true)
    } else if (submitting && currentUserStatus === SUCCEEDED) {
      goToNextStep()
    } else if (submitting && currentUserStatus === ERROR) {
      setSubmitting(false)
      openErrorNotification()
    }
  }, [currentUserStatus, setSubmitting, goToNextStep, submitting])

  const openErrorNotification = () => {
    notification.error({
      message: 'An Error Occurred',
      description: 'Please try again or contact support.',
      placement: 'topRight',
    })
  }

  const handleContinue = async () => {
    const strippedIds = Object.values(licenseData).map(license => license.id)
    try {
      await formRef.current.validateFields()
      const license_ids = strippedIds
      track({
        eventName: `Account Setup ${nextStepName} Button Clicked ${stepName}`,
        payload: { license_ids: strippedIds.join(', ') },
      })
      dispatch(updateUser(
        { license_ids },
        USER_INFORMATION_UPDATE,
        true,
        { license: Object.values(licenseData) },
      ))
    } catch ({ errorFields }) {
      if (errorFields.length) {
        const errorText = errorFields.length > 1 ? 'errors' : 'error'
        message.error({
          content: `${errorFields.length} ${errorText} found`,
          style: { marginTop: '70vh' },
        })
      }
    }
  }

  const clearFieldErrors = (changedValues) => {
    const updatedFields = Object.keys(changedValues)
      .filter((name) => formRef.current.getFieldError(name).length)
      .map((name) => ({ name, errors: [] }))
    formRef.current.setFields(updatedFields)
  }

  const handleBack = () => {
    track({ eventName: `Account Setup Back Button Clicked ${stepName}` })
    goToPreviousStep()
  }

  return (
    <div className="credentials">
      <Card>
        <Form
          ref={formRef}
          validateTrigger="onSubmit"
          onValuesChange={clearFieldErrors}
        >
          <Title level={5}>Credentials</Title>
          <LicenseSetup
            existingLicenses={licenses}
            updateLicenses={setLicenseData}
          />
        </Form>
      </Card>

      <Card style={{ marginTop: '5px' }}>
        <div className="controls">
          <Button name="back" onClick={handleBack} type="default">
            Back
          </Button>

          <Button
            name="continue"
            disabled={submitting}
            onClick={handleContinue}
            type="primary"
          >
            {submitting ? <Spin /> : nextStepName}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default Credentials
